import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  deleteSavedAssociation,
  encodeBatch,
  eraseSessionData,
  fetchAuthConfig,
  fetchCurrentUser,
  fetchKnownNumbers,
  fetchPrivacyNotice,
  fetchSavedAssociations,
  fetchSessionData,
  logout,
  saveAssociation,
  updateProductEmails,
  validateAssociation,
} from "./api";
import { KnownNumberPicker } from "./KnownNumberPicker";
import { KnownNumberInput } from "./KnownNumberInput";
import { UiLanguageMenu } from "./UiLanguageMenu";
import { useUiLanguage } from "./uiLanguageContext";
import {
  DEFAULT_ENCODING_SYSTEM,
  ENCODING_PROFILES,
  encodingSystemOrDefault,
  getEncodingProfile,
} from "./encodingProfiles";
import { getEncodingHelp } from "./helpFeatures";
import { getPrivacyContent, getTermsContent } from "./legalContent";
import {
  knownNumberWikipediaCacheKey,
  resolveKnownNumberWikipediaTitle,
} from "./knownNumberWikipedia";
import {
  flattenKnownNumberEntries,
  savedAssociationsAsKnownNumbers,
  startupKnownNumberSelection,
} from "./knownNumbers";
import {
  associationMatchesResult,
  associationMappingAsEncodeItem,
  fetchWikipediaSummary,
  fetchExactWikipediaSummary,
  normalizeNumberInput,
  parseNumberLines,
  savedAssociationAsEncodeItem,
  savedAssociationsToCsv,
  shortenWikipediaExtract,
  wikipediaCacheKey,
  wikipediaPageUrl,
  wikipediaTitleFromPageUrl,
} from "./lib/nummem";
import {
  clearPendingMappingShare,
  copyMappingShareUrl,
  createMappingShareUrl,
  MAPPING_AUTH_COMPLETE_PARAMETER,
  readPendingMappingShare,
  readMappingShareHash,
  storePendingMappingShare,
  type SharedMapping,
} from "./shareMapping";
import type {
  AuthConfig,
  BatchEncodeItem,
  Candidate,
  EncodingSystem,
  KnownNumberEntry,
  PrivacyNotice,
  SavedAssociation,
  User,
  WikipediaSummary,
} from "./types";

const MIN_NUMBER_PANEL_WIDTH = 16;
const MIN_MAJOR_CODE_PANEL_WIDTH = 33;
const DEFAULT_NUMBER_PANEL_WIDTH = 33;
const NUMBER_HEADER_CONTROL_GAP = 16;
const WORD_PREVIEW_CLOSE_DELAY_MS = 1600;
const MAJOR_HELP_WINDOW_ID = "major-help";
const MODE_HELP_WINDOW_ID = "mode-help";
const SYSTEM_HELP_WINDOW_ID = "system-help";
const KNOWN_NUMBER_WINDOW_ID = "known-number-preview";
const ENCODING_SYSTEM_STORAGE_KEY = "nummem.encodingSystem";
const CMUDICT_IMPLICIT_DIALECT = "US English (CMUdict; implicit)";
const TERMS_VERSION = "2026-08-26";
const SAMPLE_INPUT = [
  "3141592653589793",
  "299792458",
  "1618033988749895",
  "1643",
  "1915",
  "331",
  "9223372036854775807",
].join("\n");

type WikiCache = Record<string, WikipediaSummary | null | undefined>;
type NecessaryCookieChoice = "undecided" | "accepted" | "declined";
type AccountTab = "profile" | "data" | "terms" | "privacy" | "delete";
type EncodingMode = "optimal" | "random";
type InputActionNotice = { id: number; text: string };

function pronunciationDialectLabel(dialect: string | null | undefined) {
  return dialect === CMUDICT_IMPLICIT_DIALECT ? "US English" : dialect;
}

function browserSessionStorage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function persistedEncodingSystem(): EncodingSystem {
  try {
    const value = window.localStorage.getItem(ENCODING_SYSTEM_STORAGE_KEY);
    return encodingSystemOrDefault(value);
  } catch {
    return DEFAULT_ENCODING_SYSTEM;
  }
}

function persistEncodingSystem(encodingSystem: EncodingSystem) {
  try {
    window.localStorage.setItem(ENCODING_SYSTEM_STORAGE_KEY, encodingSystem);
  } catch {
    return;
  }
}

async function validatedSharedItems(mapping: SharedMapping): Promise<BatchEncodeItem[]> {
  if (getEncodingProfile(mapping.encodingSystem).sharePayloadVersion === 1) {
    return mapping.items;
  }
  const associations = await Promise.all(mapping.items.map(async (item) => {
    if (!item.ok) {
      throw new Error("Invalid or unsupported share link");
    }
    return validateAssociation({
      encoding_system: mapping.encodingSystem,
      digits: item.result.digits,
      chunks: item.result.chunks,
      candidates: item.result.candidates,
    });
  }));
  return associations.map((association) =>
    associationMappingAsEncodeItem(association, mapping.mode === "random")
  );
}

function App() {
  const { language: uiLanguage, text: t } = useUiLanguage();
  const uiTextRef = useRef(t);
  uiTextRef.current = t;
  const initialLegalView = useMemo(
    () => new URLSearchParams(window.location.search).get("legal"),
    [],
  );
  const initialNavigation = useMemo(() => {
    const directShareState = readMappingShareHash(window.location.hash);
    const authReturn = new URLSearchParams(window.location.search)
      .get(MAPPING_AUTH_COMPLETE_PARAMETER) === "complete";
    const pendingShareState = authReturn
      ? readPendingMappingShare(browserSessionStorage())
      : { kind: "none" } as const;
    const shareState = directShareState.kind !== "none"
      ? directShareState
      : pendingShareState.kind === "valid"
        ? { kind: "valid" as const, mapping: pendingShareState.mapping }
        : directShareState;
    return {
      authReturn,
      pendingShareHash: pendingShareState.kind === "valid" ? pendingShareState.hash : null,
      shareState,
    };
  }, []);
  const initialShareState = initialNavigation.shareState;
  const initialSharedMapping = initialShareState.kind === "valid"
    ? initialShareState.mapping
    : null;
  const initialSharedMappingRequiresValidation = initialSharedMapping !== null &&
    getEncodingProfile(initialSharedMapping.encodingSystem).sharePayloadVersion === 2;
  const [user, setUser] = useState<User | null>(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig>({
    admin_only_access: true,
    google_enabled: false,
    registration_mode: "invite_only",
    saved_association_limit: 10_000,
    number_row_limit: 10,
  });
  const [privacyNotice, setPrivacyNotice] = useState<PrivacyNotice | null>(null);
  const [privacyOpen, setPrivacyOpen] = useState(
    initialLegalView === "terms" || initialLegalView === "privacy",
  );
  const [contactsOpen, setContactsOpen] = useState(initialLegalView === "contacts");
  const [mappingShareUrl, setMappingShareUrl] = useState<string | null>(null);
  const [accountTab, setAccountTab] = useState<AccountTab>(
    initialLegalView === "terms" ? "terms" : "privacy",
  );
  const [activeHoverWindow, setActiveHoverWindow] =
    useState<string | null>(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [cookieChoice, setCookieChoice] =
    useState<NecessaryCookieChoice>("undecided");
  const [eraseConfirmation, setEraseConfirmation] = useState(false);
  const [privacyStatus, setPrivacyStatus] = useState("");
  const [wikipediaPreviewsEnabled, setWikipediaPreviewsEnabled] = useState(true);
  const [encodingMode, setEncodingMode] = useState<EncodingMode>(
    initialSharedMapping?.mode ?? "random",
  );
  const [encodingSystem, setEncodingSystem] = useState<EncodingSystem>(
    initialSharedMapping?.encodingSystem ?? persistedEncodingSystem(),
  );
  const [numberPanelWidth, setNumberPanelWidth] = useState(
    DEFAULT_NUMBER_PANEL_WIDTH,
  );
  const [numberInputHeight, setNumberInputHeight] = useState<number | null>(null);
  const [minimumNumberPanelWidth, setMinimumNumberPanelWidth] = useState(
    MIN_NUMBER_PANEL_WIDTH,
  );
  const [remappingRows, setRemappingRows] = useState<Set<number>>(new Set());
  const [associationBusyRows, setAssociationBusyRows] = useState<Set<number>>(
    new Set(),
  );
  const [associationHoverRow, setAssociationHoverRow] = useState<number | null>(null);
  const [input, setInput] = useState(
    initialSharedMapping?.items.map(({ digits }) => digits).join("\n") ?? SAMPLE_INPUT,
  );
  const [stripNonNumeric, setStripNonNumeric] = useState(true);
  const [inputActionNotice, setInputActionNotice] =
    useState<InputActionNotice | null>(null);
  const [readyReturnVersion, setReadyReturnVersion] = useState(0);
  const [knownNumbers, setKnownNumbers] = useState<KnownNumberEntry[]>([]);
  const [knownNumbersReady, setKnownNumbersReady] = useState(false);
  const [savedAssociations, setSavedAssociations] = useState<SavedAssociation[]>([]);
  const [selectedKnownNumbers, setSelectedKnownNumbers] =
    useState<KnownNumberEntry[]>([]);
  const [items, setItems] = useState<BatchEncodeItem[]>(
    initialSharedMappingRequiresValidation ? [] : initialSharedMapping?.items ?? [],
  );
  const [sharedMappingActive, setSharedMappingActive] = useState(
    initialSharedMapping !== null,
  );
  const [status, setStatus] = useState(
    initialShareState.kind === "invalid"
      ? t("invalidShare")
      : initialSharedMappingRequiresValidation
        ? t("validatingSharedMapping")
        : initialSharedMapping
        ? t("loadedSharedMapping")
        : t("ready"),
  );
  const [wikiCache, setWikiCache] = useState<WikiCache>({});
  const previousUiLanguage = useRef(uiLanguage);
  const encodeTimer = useRef<number | undefined>(undefined);
  const encodeVersion = useRef(0);
  const profileGeneration = useRef(0);
  const editorGrid = useRef<HTMLDivElement | null>(null);
  const inputActionVersion = useRef(0);
  const mappingShareButton = useRef<HTMLButtonElement | null>(null);
  const mappingShareFocusReturn = useRef(false);
  const startupSelectionApplied = useRef(initialSharedMapping !== null);
  const numberRowLimit = useRef(authConfig.number_row_limit);
  const helpOpen = activeHoverWindow === MAJOR_HELP_WINDOW_ID;
  const modeHelpOpen = activeHoverWindow === MODE_HELP_WINDOW_ID;
  const systemHelpOpen = activeHoverWindow === SYSTEM_HELP_WINDOW_ID;

  const parsedNumbers = useMemo(() => parseNumberLines(input), [input]);
  const savedAssociationsByRow = useMemo(
    () =>
      items.map((item, index) =>
        item?.ok && item.digits === parsedNumbers[index]
          ? savedAssociations.find((association) =>
              associationMatchesResult(association, item.result),
            )
          : undefined,
      ),
    [items, parsedNumbers, savedAssociations],
  );
  const numberPickerEntries = useMemo(
    () => [
      ...savedAssociationsAsKnownNumbers(savedAssociations, encodingSystem, {
        group: t("savedNumbers"),
        mapping: t("savedMapping"),
        subgroup: t("myAssociations"),
      }),
      ...knownNumbers,
    ],
    [encodingSystem, knownNumbers, savedAssociations, t],
  );
  const mappingShareReady = useMemo(
    () =>
      parsedNumbers.length > 0 &&
      remappingRows.size === 0 &&
      items.length === parsedNumbers.length &&
      items.every((item, index) =>
        item.ok && item.digits === parsedNumbers[index],
      ),
    [items, parsedNumbers, remappingRows],
  );

  useEffect(() => {
    persistEncodingSystem(encodingSystem);
  }, [encodingSystem]);

  useEffect(() => {
    if (previousUiLanguage.current === uiLanguage) return;
    previousUiLanguage.current = uiLanguage;
    setInputActionNotice(null);
    setPrivacyStatus("");
    setStatus(t("ready"));
  }, [t, uiLanguage]);

  const runEncode = useCallback(async (
    numbers: string[],
    selections: KnownNumberEntry[] = [],
  ) => {
    const requestVersion = ++encodeVersion.current;
    if (numbers.length === 0) {
      setItems([]);
      setStatus(uiTextRef.current("ready"));
      return;
    }
    setStatus(uiTextRef.current("encoding"));
    setRemappingRows(new Set());
    try {
      const savedByDigits = new Map(
        selections
          .filter((entry) => entry.saved_association)
          .map((entry) => [entry.saved_association!.digits, entry.saved_association!]),
      );
      const numbersToEncode = numbers.filter((digits) => !savedByDigits.has(digits));
      const response = numbersToEncode.length > 0
        ? await encodeBatch(
            numbersToEncode,
            encodingMode === "random"
              ? {
                  encoding_system: encodingSystem,
                  method: "random",
                  stochastic: true,
                }
              : { encoding_system: encodingSystem, method: "optimal" },
          )
        : { encoding_system: encodingSystem, items: [] };
      if (requestVersion !== encodeVersion.current) {
        return;
      }
      const encodedItems = new Map(response.items.map((item) => [item.digits, item]));
      setItems(numbers.map((digits) => {
        const saved = savedByDigits.get(digits);
        return saved ? savedAssociationAsEncodeItem(saved) : encodedItems.get(digits)!;
      }));
      const savedDigits = [...savedByDigits.keys()];
      setStatus(savedDigits.length === 1
        ? uiTextRef.current("loadedSavedAssociation", { digits: savedDigits[0] })
        : savedDigits.length > 1
          ? uiTextRef.current("loadedSavedAssociations", { count: savedDigits.length })
          : uiTextRef.current("ready"));
    } catch (error) {
      if (requestVersion !== encodeVersion.current) {
        return;
      }
      setStatus(
        error instanceof Error
          ? error.message
          : uiTextRef.current("encodingFailed"),
      );
    }
  }, [encodingMode, encodingSystem]);

  const applySharedMapping = useCallback(async (mapping: SharedMapping) => {
    window.clearTimeout(encodeTimer.current);
    const requestVersion = ++encodeVersion.current;
    profileGeneration.current += 1;
    startupSelectionApplied.current = true;
    setEncodingMode(mapping.mode);
    setEncodingSystem(mapping.encodingSystem);
    setInput(mapping.items.map(({ digits }) => digits).join("\n"));
    setItems([]);
    setSavedAssociations([]);
    setSelectedKnownNumbers([]);
    setWikiCache({});
    setRemappingRows(new Set());
    setAssociationBusyRows(new Set());
    setSharedMappingActive(true);
    setStatus(
      getEncodingProfile(mapping.encodingSystem).sharePayloadVersion === 2
        ? uiTextRef.current("validatingSharedMapping")
        : uiTextRef.current("loadedSharedMapping"),
    );
    try {
      const verifiedItems = await validatedSharedItems(mapping);
      if (requestVersion !== encodeVersion.current) {
        return;
      }
      setItems(verifiedItems);
      setStatus(uiTextRef.current("loadedSharedMapping"));
      void fetchSavedAssociations(mapping.encodingSystem)
        .then((response) => {
          if (requestVersion === encodeVersion.current) {
            setSavedAssociations(response.items);
          }
        })
        .catch(() => undefined);
    } catch {
      if (requestVersion !== encodeVersion.current) {
        return;
      }
      setItems([]);
      setStatus(uiTextRef.current("invalidShare"));
    }
  }, []);

  const updatePanelWidth = useCallback((clientX: number) => {
    const bounds = editorGrid.current?.getBoundingClientRect();
    if (!bounds || bounds.width === 0) {
      return;
    }
    const nextWidth = ((clientX - bounds.left) / bounds.width) * 100;
    setNumberPanelWidth(
      Math.min(
        100 - MIN_MAJOR_CODE_PANEL_WIDTH,
        Math.max(minimumNumberPanelWidth, nextWidth),
      ),
    );
  }, [minimumNumberPanelWidth]);

  function handleDividerPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    updatePanelWidth(event.clientX);
  }

  function handleDividerPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      updatePanelWidth(event.clientX);
    }
  }

  function handleDividerKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    const step = event.shiftKey ? 5 : 1;
    let nextWidth = numberPanelWidth;
    if (event.key === "ArrowLeft") {
      nextWidth -= step;
    } else if (event.key === "ArrowRight") {
      nextWidth += step;
    } else if (event.key === "Home") {
      nextWidth = minimumNumberPanelWidth;
    } else if (event.key === "End") {
      nextWidth = 100 - MIN_MAJOR_CODE_PANEL_WIDTH;
    } else {
      return;
    }
    event.preventDefault();
    setNumberPanelWidth(
      Math.min(
        100 - MIN_MAJOR_CODE_PANEL_WIDTH,
        Math.max(minimumNumberPanelWidth, nextWidth),
      ),
    );
  }

  useEffect(() => {
    const grid = editorGrid.current;
    const header = grid?.querySelector<HTMLElement>(".number-panel-header");
    if (!grid || !header) {
      return;
    }
    const updateMinimumWidth = () => {
      const gridBounds = grid.getBoundingClientRect();
      const headerBounds = header.getBoundingClientRect();
      if (gridBounds.width === 0) {
        return;
      }
      const requiredWidth =
        ((headerBounds.right - gridBounds.left + NUMBER_HEADER_CONTROL_GAP) /
          gridBounds.width) *
        100;
      const nextMinimum = Math.min(
        100 - MIN_MAJOR_CODE_PANEL_WIDTH,
        requiredWidth,
      );
      setMinimumNumberPanelWidth(nextMinimum);
      setNumberPanelWidth((width) => Math.max(nextMinimum, width));
    };
    updateMinimumWidth();
    const observer = new ResizeObserver(updateMinimumWidth);
    observer.observe(grid);
    observer.observe(header);
    return () => observer.disconnect();
  }, [knownNumbersReady, user]);

  function closeHoverWindow(windowId: string) {
    setActiveHoverWindow((activeWindow) =>
      activeWindow === windowId ? null : activeWindow,
    );
  }

  function toggleHoverWindow(windowId: string) {
    setActiveHoverWindow((activeWindow) =>
      activeWindow === windowId ? null : windowId,
    );
  }

  useEffect(() => {
    fetchAuthConfig()
      .then((config) => {
        numberRowLimit.current = config.number_row_limit;
        setAuthConfig(config);
      })
      .catch(() => undefined);
    fetchPrivacyNotice().then(setPrivacyNotice).catch(() => undefined);
    fetchCurrentUser()
      .then((currentUser) => {
        setUser(currentUser);
        if (!initialNavigation.authReturn) {
          return;
        }
        clearPendingMappingShare(browserSessionStorage());
        const url = new URL(window.location.href);
        url.searchParams.delete(MAPPING_AUTH_COMPLETE_PARAMETER);
        if (initialNavigation.pendingShareHash !== null) {
          url.hash = initialNavigation.pendingShareHash.slice(1);
        }
        window.history.replaceState(null, "", url);
      })
      .catch(() => undefined);
  }, [initialNavigation]);

  useEffect(() => {
    const handleSharedMappingNavigation = () => {
      const shareState = readMappingShareHash(window.location.hash);
      if (shareState.kind === "invalid") {
        setStatus(t("invalidShare"));
        return;
      }
      if (shareState.kind !== "valid") {
        return;
      }
      void applySharedMapping(shareState.mapping);
    };
    window.addEventListener("hashchange", handleSharedMappingNavigation);
    return () => window.removeEventListener("hashchange", handleSharedMappingNavigation);
  }, [applySharedMapping, t]);

  useEffect(() => {
    if (!user) {
      return;
    }
    const shareState = readMappingShareHash(window.location.hash);
    if (
      shareState.kind === "valid" &&
      getEncodingProfile(shareState.mapping.encodingSystem).sharePayloadVersion === 2
    ) {
      const validationTimer = window.setTimeout(() => {
        void applySharedMapping(shareState.mapping);
      }, 0);
      return () => window.clearTimeout(validationTimer);
    }
  }, [applySharedMapping, user]);

  useEffect(() => {
    if (mappingShareUrl === null && mappingShareFocusReturn.current) {
      mappingShareFocusReturn.current = false;
      mappingShareButton.current?.focus();
    }
  }, [mappingShareUrl]);

  useEffect(() => {
    if (!user) {
      return;
    }
    let cancelled = false;
    fetchKnownNumbers()
      .then((response) => {
        if (!cancelled) {
          setKnownNumbers(response.items);
          if (!startupSelectionApplied.current) {
            const startupSelection = startupKnownNumberSelection(
              response.items,
              numberRowLimit.current,
            );
            startupSelectionApplied.current = true;
            setSelectedKnownNumbers(startupSelection);
            setInput(flattenKnownNumberEntries(startupSelection).join("\n"));
          }
          setKnownNumbersReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setKnownNumbers([]);
          setKnownNumbersReady(true);
        }
      });
    fetchSavedAssociations(encodingSystem)
      .then((response) => {
        if (!cancelled) {
          setSavedAssociations(response.items);
          setAuthConfig((config) => ({
            ...config,
            saved_association_limit: response.limit,
          }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSavedAssociations([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [encodingSystem, user]);

  useEffect(() => {
    if (!user || sharedMappingActive) {
      return;
    }
    window.clearTimeout(encodeTimer.current);
    encodeTimer.current = window.setTimeout(() => {
      void runEncode(parsedNumbers, selectedKnownNumbers);
    }, 250);
    return () => window.clearTimeout(encodeTimer.current);
  }, [parsedNumbers, runEncode, selectedKnownNumbers, sharedMappingActive, user]);

  async function handleLogout() {
    await logout();
    setUser(null);
    setActiveHoverWindow(null);
    setHelpDialogOpen(false);
    setItems([]);
    setKnownNumbers([]);
    setKnownNumbersReady(false);
    setSavedAssociations([]);
    setSelectedKnownNumbers([]);
    setEncodingSystem(DEFAULT_ENCODING_SYSTEM);
    setSharedMappingActive(false);
    startupSelectionApplied.current = false;
    setInput(SAMPLE_INPUT);
  }

  async function handleDataExport() {
    setPrivacyStatus("");
    try {
      const data = await fetchSessionData();
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "nummem-session-data.json";
      link.click();
      URL.revokeObjectURL(url);
      setPrivacyStatus(t("sessionDataExported"));
    } catch (error) {
      setPrivacyStatus(error instanceof Error ? error.message : t("exportFailed"));
    }
  }

  function handleSavedAssociationsCsvExport() {
    const profile = getEncodingProfile(encodingSystem);
    const url = URL.createObjectURL(
      new Blob([savedAssociationsToCsv(savedAssociations)], {
        type: "text/csv;charset=utf-8",
      }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = profile.savedAssociationsExportFilename;
    link.click();
    URL.revokeObjectURL(url);
    setPrivacyStatus(t("savedNumbersExported"));
  }

  function openAccount(tab: AccountTab) {
    setAccountTab(tab);
    setPrivacyStatus("");
    setPrivacyOpen(true);
  }

  function openPublicPrivacy() {
    setAccountTab("privacy");
    setPrivacyStatus("");
    setPrivacyOpen(true);
    window.history.pushState(null, "", "/?legal=privacy");
  }

  function openPublicTerms() {
    setAccountTab("terms");
    setPrivacyStatus("");
    setPrivacyOpen(true);
    window.history.pushState(null, "", "/?legal=terms");
  }

  function closePrivacy() {
    setPrivacyOpen(false);
    setEraseConfirmation(false);
    setPrivacyStatus("");
    if (["privacy", "terms"].includes(
      new URLSearchParams(window.location.search).get("legal") ?? "",
    )) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.hash}`);
    }
  }

  function openContacts() {
    setPrivacyOpen(false);
    setContactsOpen(true);
    window.history.pushState(null, "", "/?legal=contacts");
  }

  function closeContacts() {
    setContactsOpen(false);
    if (new URLSearchParams(window.location.search).get("legal") === "contacts") {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.hash}`);
    }
  }

  function handleNumberInputChange(
    value: string,
    filterNonNumeric = stripNonNumeric,
  ) {
    const normalized = normalizeNumberInput(value, {
      limit: authConfig.number_row_limit,
      stripNonNumeric: filterNonNumeric,
    });
    setInput(normalized.value);
    const actions: string[] = [];
    if (normalized.nonNumericCharactersRemoved > 0) {
      actions.push(
        t(
          normalized.nonNumericCharactersRemoved === 1
            ? "removeNonNumericOne"
            : "removeNonNumericMany",
          { count: normalized.nonNumericCharactersRemoved },
        ),
      );
    }
    if (normalized.duplicateRowsRemoved > 0) {
      actions.push(
        t(
          normalized.duplicateRowsRemoved === 1
            ? "removedDuplicateOne"
            : "removedDuplicateMany",
          { count: normalized.duplicateRowsRemoved },
        ),
      );
    }
    if (normalized.truncated) {
      actions.push(t("numberRowLimit", { count: authConfig.number_row_limit }));
    }
    if (actions.length > 0) {
      showInputActionNotice(actions.join("\t"));
    }
  }

  function handleManualNumberInputChange(value: string) {
    startupSelectionApplied.current = true;
    setSharedMappingActive(false);
    setSelectedKnownNumbers((entries) =>
      entries.filter((entry) => entry.saved_association),
    );
    handleNumberInputChange(value);
  }

  function handleKnownNumberToggle(entry: KnownNumberEntry) {
    startupSelectionApplied.current = true;
    setSharedMappingActive(false);
    const selected = selectedKnownNumbers.some(
      (candidate) => candidate.id === entry.id,
    );
    const next = selected
      ? selectedKnownNumbers.filter((candidate) => candidate.id !== entry.id)
      : [...selectedKnownNumbers, entry];
    if (flattenKnownNumberEntries(next).length > authConfig.number_row_limit) {
      showInputActionNotice(
        t("knownSelectionLimit", { count: authConfig.number_row_limit }),
      );
      return;
    }
    setSelectedKnownNumbers(next);
    handleNumberInputChange(flattenKnownNumberEntries(next).join("\n"));
  }

  function showInputActionNotice(text: string) {
    setReadyReturnVersion(0);
    setInputActionNotice({
      id: ++inputActionVersion.current,
      text,
    });
  }

  function handleClearNumberInput() {
    startupSelectionApplied.current = true;
    setSharedMappingActive(false);
    setSelectedKnownNumbers([]);
    setInput("");
    showInputActionNotice(t("clearedInput"));
  }

  function handleStripNonNumericChange(enabled: boolean) {
    setStripNonNumeric(enabled);
    if (enabled) {
      handleNumberInputChange(input, true);
    }
  }

  async function handleSessionErasure() {
    if (!eraseConfirmation) {
      setEraseConfirmation(true);
      return;
    }
    try {
      await eraseSessionData();
      setUser(null);
      setActiveHoverWindow(null);
      setHelpDialogOpen(false);
      setItems([]);
      setKnownNumbers([]);
      setKnownNumbersReady(false);
      setSavedAssociations([]);
      setSelectedKnownNumbers([]);
      setEncodingSystem(DEFAULT_ENCODING_SYSTEM);
      startupSelectionApplied.current = false;
      setCookieChoice("undecided");
      setEraseConfirmation(false);
      setPrivacyOpen(false);
    } catch (error) {
      setPrivacyStatus(error instanceof Error ? error.message : t("erasureFailed"));
    }
  }

  async function handleProductEmails(enabled: boolean) {
    setPrivacyStatus("");
    try {
      await updateProductEmails(enabled);
      setUser((current) =>
        current ? { ...current, product_emails_opt_in: enabled } : current,
      );
      setPrivacyStatus(
        enabled ? t("productEmailsEnabled") : t("productEmailsDisabled"),
      );
    } catch (error) {
      setPrivacyStatus(
        error instanceof Error ? error.message : t("preferenceUpdateFailed"),
      );
    }
  }

  function handleMappingShare() {
    if (!mappingShareReady) {
      setStatus(t("waitForMapping"));
      return;
    }
    try {
      const url = createMappingShareUrl(
        window.location.href,
        items,
        encodingMode,
        encodingSystem,
      );
      setMappingShareUrl(url);
      setStatus(t("shareLinkReady"));
    } catch (error) {
      setStatus(error instanceof Error ? error.message : t("mappingShareFailed"));
    }
  }

  function closeMappingShare() {
    mappingShareFocusReturn.current = true;
    setMappingShareUrl(null);
  }

  async function loadWiki(word: string, wikipediaUrl?: string | null) {
    const requestedSystem = encodingSystem;
    const requestedGeneration = profileGeneration.current;
    const cacheKey = wikipediaCacheKey(requestedSystem, word, wikipediaUrl);
    if (cacheKey in wikiCache) {
      return;
    }
    setWikiCache((cache) => ({ ...cache, [cacheKey]: undefined }));
    const exactTitle = wikipediaTitleFromPageUrl(wikipediaUrl, requestedSystem);
    const summary = await fetchWikipediaSummary(
      exactTitle ?? word,
      requestedSystem,
    );
    if (requestedGeneration !== profileGeneration.current) {
      return;
    }
    setWikiCache((cache) => ({ ...cache, [cacheKey]: summary }));
  }

  async function loadKnownNumberWiki(entry: KnownNumberEntry) {
    const requestedSystem = encodingSystem;
    if (
      getEncodingProfile(requestedSystem).knownNumberWikipediaIdentity.kind ===
      "catalog-label"
    ) {
      return loadWiki(entry.label);
    }
    const requestedGeneration = profileGeneration.current;
    const cacheKey = knownNumberWikipediaCacheKey(requestedSystem, entry);
    if (cacheKey in wikiCache) {
      return;
    }
    setWikiCache((cache) => ({ ...cache, [cacheKey]: undefined }));
    const title = await resolveKnownNumberWikipediaTitle(
      entry.label,
      requestedSystem,
    );
    if (requestedGeneration !== profileGeneration.current) {
      return;
    }
    if (title === null) {
      setWikiCache((cache) => ({ ...cache, [cacheKey]: null }));
      return;
    }
    const summary = await fetchExactWikipediaSummary(title, requestedSystem);
    if (requestedGeneration !== profileGeneration.current) {
      return;
    }
    setWikiCache((cache) => ({ ...cache, [cacheKey]: summary }));
  }

  async function handleRandomRemap(index: number, digits: string) {
    const requestVersion = encodeVersion.current;
    setRemappingRows((rows) => new Set(rows).add(index));
    setStatus(t("remapping", { digits }));
    try {
      const response = await encodeBatch([digits], {
        encoding_system: encodingSystem,
        method: "random",
        stochastic: true,
      });
      const replacement = response.items[0];
      if (replacement && requestVersion === encodeVersion.current) {
        setItems((current) =>
          current.map((item, itemIndex) =>
            itemIndex === index && item.digits === digits ? replacement : item,
          ),
        );
      }
      if (requestVersion === encodeVersion.current) {
        setStatus(t("ready"));
      }
    } catch (error) {
      if (requestVersion === encodeVersion.current) {
        setStatus(error instanceof Error ? error.message : t("remappingFailed"));
      }
    } finally {
      if (requestVersion === encodeVersion.current) {
        setRemappingRows((rows) => {
          const next = new Set(rows);
          next.delete(index);
          return next;
        });
      }
    }
  }

  async function handleAssociationToggle(
    index: number,
    item: BatchEncodeItem | undefined,
    savedAssociation: SavedAssociation | undefined,
  ) {
    if (!item?.ok || associationBusyRows.has(index)) {
      return;
    }
    const requestGeneration = profileGeneration.current;
    setAssociationBusyRows((rows) => new Set(rows).add(index));
    try {
      if (savedAssociation) {
        await deleteSavedAssociation(savedAssociation.id);
        if (requestGeneration !== profileGeneration.current) {
          return;
        }
        setSavedAssociations((associations) =>
          associations.filter((association) => association.id !== savedAssociation.id),
        );
        setSelectedKnownNumbers((entries) =>
          entries.filter(
            (entry) => entry.saved_association?.id !== savedAssociation.id,
          ),
        );
        setStatus(t("removedSavedAssociation", { digits: item.digits }));
      } else {
        const saved = await saveAssociation({
          encoding_system: encodingSystem,
          digits: item.result.digits,
          chunks: item.result.chunks,
          candidates: item.result.candidates,
        });
        if (requestGeneration !== profileGeneration.current) {
          return;
        }
        setSavedAssociations((associations) => [
          ...associations.filter((association) => association.id !== saved.id),
          saved,
        ]);
        setStatus(t("savedAssociationCreated", { digits: item.digits }));
      }
    } catch (error) {
      if (requestGeneration === profileGeneration.current) {
        setStatus(
          error instanceof Error ? error.message : t("associationUpdateFailed"),
        );
      }
    } finally {
      if (requestGeneration === profileGeneration.current) {
        setAssociationBusyRows((rows) => {
          const next = new Set(rows);
          next.delete(index);
          return next;
        });
      }
    }
  }

  function handleEncodingModeChange(nextMode: EncodingMode) {
    if (nextMode === encodingMode) {
      return;
    }
    window.clearTimeout(encodeTimer.current);
    encodeVersion.current += 1;
    setEncodingMode(nextMode);
    setItems([]);
    setRemappingRows(new Set());
    setSharedMappingActive(false);
    setStatus(t("encoding"));
  }

  function handleEncodingSystemChange(nextSystem: EncodingSystem) {
    if (nextSystem === encodingSystem) {
      return;
    }
    window.clearTimeout(encodeTimer.current);
    encodeVersion.current += 1;
    profileGeneration.current += 1;
    setActiveHoverWindow(null);
    setWikiCache({});
    setEncodingSystem(nextSystem);
    setSavedAssociations([]);
    setSelectedKnownNumbers((entries) =>
      entries.filter((entry) => !entry.saved_association),
    );
    setItems([]);
    setRemappingRows(new Set());
    setAssociationBusyRows(new Set());
    setSharedMappingActive(false);
    setStatus(t("usingProfile", { name: t(nextSystem) }));
  }

  const encodingHelp = getEncodingHelp(encodingSystem, uiLanguage);

  return (
    <main className="app-shell">
      <header className="topbar" aria-label={t("appHeader")}>
        <div className="topbar-main">
          {user ? (
            <nav className="main-menu" aria-label={t("mainMenu")}>
              <button
                className="main-menu-button"
                type="button"
                onClick={() => setHelpDialogOpen(true)}
              >
                {t("help")}
              </button>
            </nav>
          ) : null}
          <UiLanguageMenu />
          {user ? (
            <div className="session-pill" aria-label={t("signedInSession")}>
              <span>{user.email}</span>
              {user.role === "admin" ? <span>{user.role}</span> : null}
              <button
                className="icon-button account-button"
                type="button"
                aria-label={t("userAccount")}
                title={t("userAccount")}
                onClick={() => openAccount("profile")}
              >
                <svg aria-hidden="true" viewBox="0 0 24 24">
                  <circle cx="12" cy="8" r="3.5" />
                  <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
                </svg>
              </button>
              <button className="secondary-button" type="button" onClick={handleLogout}>
                {t("signOut")}
              </button>
            </div>
          ) : null}
        </div>
        <div className="brand-row">
          <div
            className="brand-cluster"
            onMouseLeave={() => closeHoverWindow(MAJOR_HELP_WINDOW_ID)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                closeHoverWindow(MAJOR_HELP_WINDOW_ID);
              }
            }}
          >
            <a className="brand" href="/" aria-label={t("nummemHome")}>
              <img
                className="brand-image"
                src="/assets/nummem-logo.png"
                width="1672"
                height="941"
                alt=""
              />
            </a>
            {user ? (
              <>
                <button
                  className="icon-button help-button"
                  type="button"
                  aria-label={t("majorSystemHelp")}
                  aria-expanded={helpOpen}
                  aria-controls="major-system-help"
                  title={t("majorSystemHelp")}
                  onClick={() => toggleHoverWindow(MAJOR_HELP_WINDOW_ID)}
                >
                  ?
                </button>
                {helpOpen ? (
                  <section
                    id="major-system-help"
                    className="help-panel"
                    role="region"
                    aria-label={t("majorSystemRules")}
                  >
                    <strong>{encodingHelp.compact.title}</strong>
                    <span>{encodingHelp.compact.introduction}</span>
                    <ul className="major-system-map">
                      {encodingHelp.compact.mapping.map(({ description, digit }) => (
                        <li key={digit}>
                          <strong>{digit}</strong>
                          <span>{description}</span>
                        </li>
                      ))}
                    </ul>
                    <span>{encodingHelp.compact.notes}</span>
                    <a
                      href={encodingHelp.compact.wikipediaUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("wikipedia")}
                    </a>
                  </section>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        {user ? (
          <>
            <div className="encoding-system-row">
              <div
                className="encoding-system-switch"
                role="radiogroup"
                aria-label={t("encodingSystem")}
              >
                {ENCODING_PROFILES.map((profile) => (
                  <label
                    className={
                      encodingSystem === profile.identifier
                        ? "encoding-system-segment encoding-system-segment-active"
                        : "encoding-system-segment"
                    }
                    key={profile.identifier}
                  >
                    <input
                      type="radio"
                      name="encoding-system"
                      value={profile.identifier}
                      checked={encodingSystem === profile.identifier}
                      onChange={() => handleEncodingSystemChange(profile.identifier)}
                    />
                    <span>{t(profile.identifier)}</span>
                  </label>
                ))}
              </div>
              <span
                className="system-help-shell"
                onMouseLeave={() => closeHoverWindow(SYSTEM_HELP_WINDOW_ID)}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    closeHoverWindow(SYSTEM_HELP_WINDOW_ID);
                  }
                }}
              >
                <button
                  className="system-help-button"
                  type="button"
                  aria-label={t("encodingSystemHelp")}
                  aria-expanded={systemHelpOpen}
                  onClick={() => toggleHoverWindow(SYSTEM_HELP_WINDOW_ID)}
                >
                  ?
                </button>
                {systemHelpOpen ? (
                  <span className="system-help-tooltip" role="tooltip">
                    {t("encodingSystemText")}
                  </span>
                ) : null}
              </span>
            </div>
            <div className="encoding-mode-row">
              <label className={`encoding-mode-switch encoding-mode-${encodingMode}`}>
                <span className="encoding-mode-label">{t("optimal")}</span>
                <input
                  type="checkbox"
                  role="switch"
                  aria-label={t("encodingMode")}
                  checked={encodingMode === "random"}
                  onChange={(event) =>
                    handleEncodingModeChange(
                      event.target.checked ? "random" : "optimal",
                    )
                  }
                />
                <span className="encoding-mode-track" aria-hidden="true">
                  <span className="encoding-mode-thumb" />
                </span>
                <span className="encoding-mode-label">{t("random")}</span>
              </label>
              <span
                className="mode-help-shell"
                onMouseLeave={() => closeHoverWindow(MODE_HELP_WINDOW_ID)}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    closeHoverWindow(MODE_HELP_WINDOW_ID);
                  }
                }}
              >
                <button
                  className="mode-help-button"
                  type="button"
                  aria-label={t("encodingModeHelp")}
                  aria-expanded={modeHelpOpen}
                  onClick={() => toggleHoverWindow(MODE_HELP_WINDOW_ID)}
                >
                  ?
                </button>
                {modeHelpOpen ? (
                  <span className="mode-help-tooltip" role="tooltip">
                    {t("encodingModeText")}
                  </span>
                ) : null}
              </span>
            </div>
          </>
        ) : null}
      </header>

      {!user ? (
        <section className="auth-panel" aria-label={t("authorization")}>
          <section className="cookie-choice" aria-label={t("cookies")}>
            <p>
              {t("cookieExplanation", { access: authConfig.admin_only_access
                ? t("accessAdminOnly")
                : authConfig.registration_mode === "open"
                  ? t("accessOpen")
                  : t("accessInvited") })}
            </p>
            <button
              className="text-button"
              type="button"
              onClick={() => openAccount("privacy")}
            >
              {t("readPrivacyNotice")}
            </button>
            {cookieChoice === "undecided" ? (
              <div className="choice-actions">
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => setCookieChoice("accepted")}
                >
                  {t("useNecessaryCookies")}
                </button>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setCookieChoice("declined")}
                >
                  {t("decline")}
                </button>
              </div>
            ) : null}
            {cookieChoice === "declined" ? (
              <div className="declined-message" role="status">
                <p>{t("cookieNoAuthentication")}</p>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setCookieChoice("undecided")}
                >
                  {t("reconsider")}
                </button>
              </div>
            ) : null}
          </section>
          {cookieChoice === "accepted" && authConfig.google_enabled ? (
            <a
              className="google-auth-button"
              href="/auth/google/login"
              onClick={() => storePendingMappingShare(
                window.location.hash,
                browserSessionStorage(),
              )}
            >
              {t("continueGoogle")}
            </a>
          ) : null}
          {cookieChoice === "accepted" &&
          !authConfig.google_enabled ? (
            <p className="error-text">{t("unconfiguredAuth")}</p>
          ) : null}
        </section>
      ) : !knownNumbersReady ? (
        <section className="workbench-loading" aria-label={t("loadingKnown")}>
          {t("loadingKnownAndSaved")}
        </section>
      ) : (
        <section className="workbench" aria-label={t("majorPlaygroundEncoder")}>
          <div className="mapping-toolbar">
            <KnownNumberPicker
              entries={numberPickerEntries}
              maximumNumbers={authConfig.number_row_limit}
              selectedEntries={selectedKnownNumbers}
              onClear={() => setSelectedKnownNumbers([])}
              onLimitReached={() =>
                showInputActionNotice(
                  t("knownSelectionLimit", { count: authConfig.number_row_limit }),
                )
              }
              onToggle={handleKnownNumberToggle}
            />
            <button
              className="share-mapping-button"
              ref={mappingShareButton}
              type="button"
              aria-label={t("shareCurrentMapping")}
              disabled={!mappingShareReady}
              title={mappingShareReady
                ? t("shareExactMapping")
                : t("waitForMapping")}
              onClick={handleMappingShare}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <circle cx="18" cy="5" r="2.5" />
                <circle cx="6" cy="12" r="2.5" />
                <circle cx="18" cy="19" r="2.5" />
                <path d="m8.2 10.8 7.6-4.5M8.2 13.2l7.6 4.5" />
              </svg>
              <span>{t("share")}</span>
            </button>
          </div>
          <div
            className="editor-grid"
            ref={editorGrid}
            style={{ "--numbers-width": `${numberPanelWidth}%` } as CSSProperties}
          >
            <KnownNumberInput
              activeHoverWindow={activeHoverWindow}
              entries={selectedKnownNumbers}
              encodingSystem={encodingSystem}
              hoverWindowId={KNOWN_NUMBER_WINDOW_ID}
              maximumRows={authConfig.number_row_limit}
              onChange={handleManualNumberInputChange}
              onClear={handleClearNumberInput}
              onHoverWindowClose={closeHoverWindow}
              onHoverWindowOpen={setActiveHoverWindow}
              onHoveredLineChange={setAssociationHoverRow}
              onHeightChange={setNumberInputHeight}
              onRowLimitReached={() =>
                showInputActionNotice(
                  t("numberRowLimit", { count: authConfig.number_row_limit }),
                )
              }
              onWikipediaPreview={loadKnownNumberWiki}
              onStripNonNumericChange={handleStripNonNumericChange}
              summaries={wikiCache}
              value={input}
              stripNonNumeric={stripNonNumeric}
              wikipediaPreviewsEnabled={wikipediaPreviewsEnabled}
            />
            <div
              className="resize-divider"
              role="separator"
              aria-label={t("resizeBlocks")}
              aria-orientation="vertical"
              aria-valuemin={Math.round(minimumNumberPanelWidth)}
              aria-valuemax={100 - MIN_MAJOR_CODE_PANEL_WIDTH}
              aria-valuenow={Math.round(numberPanelWidth)}
              tabIndex={0}
              title={t("resizeHelp")}
              onDoubleClick={() =>
                setNumberPanelWidth(
                  Math.max(DEFAULT_NUMBER_PANEL_WIDTH, minimumNumberPanelWidth),
                )
              }
              onKeyDown={handleDividerKeyDown}
              onPointerDown={handleDividerPointerDown}
              onPointerMove={handleDividerPointerMove}
            >
              <span className="resize-divider-grip" aria-hidden="true" />
            </div>
            <AssociationGutter
              activeRow={associationHoverRow}
              busyRows={associationBusyRows}
              digits={parsedNumbers}
              items={items}
              limit={authConfig.saved_association_limit}
              onHoverRow={setAssociationHoverRow}
              onToggle={(index) =>
                void handleAssociationToggle(
                  index,
                  items[index],
                  savedAssociationsByRow[index],
                )
              }
              savedAssociations={savedAssociationsByRow}
              total={savedAssociations.length}
            />
            <section className="result-panel" aria-label={t("encodingResults")}>
              <div className="panel-title">{t("majorCode")}</div>
              <div
                className="result-list"
                style={
                  numberInputHeight === null
                    ? undefined
                    : {
                        height: numberInputHeight,
                        minHeight: numberInputHeight,
                      }
                }
              >
                {parsedNumbers.length === 0 ? (
                  <div className="empty-row"> </div>
                ) : (
                  parsedNumbers.map((digits, index) => (
                    <ResultRow
                      activeHoverWindow={activeHoverWindow}
                      key={`${digits}-${index}`}
                      digits={digits}
                      encodingSystem={encodingSystem}
                      item={items[index]?.digits === digits ? items[index] : undefined}
                      rowIndex={index}
                      associationBusy={associationBusyRows.has(index)}
                      savedAssociation={savedAssociationsByRow[index]}
                      wikiCache={wikiCache}
                      onWordPreview={loadWiki}
                      onHoverWindowClose={closeHoverWindow}
                      onHoverWindowOpen={setActiveHoverWindow}
                      wikipediaPreviewsEnabled={wikipediaPreviewsEnabled}
                      randomRemappingEnabled={encodingMode === "random"}
                      remapping={remappingRows.has(index)}
                      onRandomRemap={() => void handleRandomRemap(index, digits)}
                      onAssociationHover={(active) =>
                        setAssociationHoverRow(active ? index : null)
                      }
                      onAssociationToggle={() =>
                        void handleAssociationToggle(
                          index,
                          items[index],
                          savedAssociationsByRow[index],
                        )
                      }
                    />
                  ))
                )}
              </div>
            </section>
          </div>
          <footer className="statusbar" aria-live="polite">
            <span className="status-message-window">
              {inputActionNotice ? (
                <span
                  className="status-message-action"
                  key={inputActionNotice.id}
                  onAnimationEnd={(event) => {
                    if (!event.animationName.startsWith("status-action-")) {
                      return;
                    }
                    setInputActionNotice(null);
                    setStatus(t("ready"));
                    setReadyReturnVersion((version) => version + 1);
                  }}
                >
                  {inputActionNotice.text}
                </span>
              ) : (
                <span
                  className={
                    readyReturnVersion > 0
                      ? "status-message-ready-return"
                      : "status-message-static"
                  }
                  key={`ready-${readyReturnVersion}`}
                >
                  {status}
                </span>
              )}
            </span>
            <label className="preview-choice">
              <input
                type="checkbox"
                checked={wikipediaPreviewsEnabled}
                onChange={(event) =>
                  setWikipediaPreviewsEnabled(event.target.checked)
                }
              />
              {t("loadWikipediaPreviews")}
            </label>
            <span>
              {t("linesCount", {
                count: parsedNumbers.length,
                limit: authConfig.number_row_limit,
              })}
            </span>
          </footer>
        </section>
      )}
      <footer className="privacy-footer">
        <span className="legal-links">
          <a
            className="text-button"
            href="/?legal=terms"
            onClick={(event) => {
              event.preventDefault();
              openPublicTerms();
            }}
          >
            {t("terms")}
          </a>
          <a
            className="text-button"
            href="/?legal=privacy"
            onClick={(event) => {
              event.preventDefault();
              openPublicPrivacy();
            }}
          >
            {t("privacy")}
          </a>
          <a
            className="text-button"
            href="/?legal=contacts"
            onClick={(event) => {
              event.preventDefault();
              openContacts();
            }}
          >
            {t("contacts")}
          </a>
        </span>
      </footer>
      {helpDialogOpen ? (
        <HelpDialog
          encodingSystem={encodingSystem}
          onClose={() => setHelpDialogOpen(false)}
        />
      ) : null}
      {contactsOpen ? (
        <ContactsDialog notice={privacyNotice} onClose={closeContacts} />
      ) : null}
      {mappingShareUrl ? (
        <ShareMappingDialog
          url={mappingShareUrl}
          onClose={closeMappingShare}
        />
      ) : null}
      {privacyOpen ? (
        <AccountDialog
          activeTab={accountTab}
          savedAssociationLimit={authConfig.saved_association_limit}
          savedAssociations={savedAssociations}
          notice={privacyNotice}
          user={user}
          productEmailsOptIn={user?.product_emails_opt_in ?? false}
          eraseConfirmation={eraseConfirmation}
          status={privacyStatus}
          onClose={closePrivacy}
          onContacts={openContacts}
          onPrivacy={openPublicPrivacy}
          onTerms={openPublicTerms}
          onExport={handleDataExport}
          onExportSavedAssociations={handleSavedAssociationsCsvExport}
          onErase={handleSessionErasure}
          onProductEmails={handleProductEmails}
          onTabChange={setAccountTab}
        />
      ) : null}
    </main>
  );
}

function ShareMappingDialog({
  onClose,
  url,
}: {
  onClose: () => void;
  url: string;
}) {
  const { text: t } = useUiLanguage();
  const [copyStatus, setCopyStatus] = useState("");
  const dialog = useRef<HTMLElement | null>(null);
  const linkInput = useRef<HTMLInputElement | null>(null);

  async function handleCopy() {
    setCopyStatus("");
    try {
      await copyMappingShareUrl(url, navigator.clipboard);
      setCopyStatus(t("linkCopied"));
    } catch {
      linkInput.current?.focus();
      linkInput.current?.select();
      setCopyStatus(t("unableToCopy"));
    }
  }

  return (
    <div className="dialog-backdrop">
      <section
        ref={dialog}
        className="share-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-dialog-title"
        aria-describedby="share-dialog-description"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onClose();
            return;
          }
          if (event.key !== "Tab") {
            return;
          }
          const focusable = Array.from(
            dialog.current?.querySelectorAll<HTMLElement>(
              "button:not([disabled]), input:not([disabled])",
            ) ?? [],
          );
          const first = focusable[0];
          const last = focusable.at(-1);
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
      >
        <div className="dialog-heading">
          <div>
            <h1 id="share-dialog-title">{t("shareMapping")}</h1>
            <p id="share-dialog-description">
              {t("copyShareDescription")}
            </p>
          </div>
          <button className="secondary-button" type="button" onClick={onClose}>
            {t("close")}
          </button>
        </div>
        <label className="share-link-label" htmlFor="mapping-share-link">
          {t("mappingShareLink")}
        </label>
        <div className="share-link-row">
          <input
            id="mapping-share-link"
            ref={linkInput}
            type="url"
            readOnly
            value={url}
            onClick={(event) => event.currentTarget.select()}
          />
          <button
            className="share-copy-button"
            type="button"
            autoFocus
            onClick={() => void handleCopy()}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <rect x="8" y="8" width="11" height="11" rx="2" />
              <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
            </svg>
            <span>{t("copyLink")}</span>
          </button>
        </div>
        <p className="share-link-notice">
          {t("shareLinkNotice")}
        </p>
        <p className="share-copy-status" aria-live="polite">{copyStatus}</p>
      </section>
    </div>
  );
}

type ResultRowProps = {
  activeHoverWindow: string | null;
  associationBusy: boolean;
  digits: string;
  encodingSystem: EncodingSystem;
  item: BatchEncodeItem | undefined;
  wikiCache: WikiCache;
  rowIndex: number;
  onHoverWindowClose: (windowId: string) => void;
  onHoverWindowOpen: (windowId: string) => void;
  onWordPreview: (word: string, wikipediaUrl?: string | null) => void;
  wikipediaPreviewsEnabled: boolean;
  randomRemappingEnabled: boolean;
  remapping: boolean;
  onRandomRemap: () => void;
  onAssociationHover: (active: boolean) => void;
  onAssociationToggle: () => void;
  savedAssociation: SavedAssociation | undefined;
};

function ResultRow({
  activeHoverWindow,
  associationBusy,
  digits,
  encodingSystem,
  item,
  wikiCache,
  rowIndex,
  onHoverWindowClose,
  onHoverWindowOpen,
  onWordPreview,
  wikipediaPreviewsEnabled,
  randomRemappingEnabled,
  remapping,
  onRandomRemap,
  onAssociationHover,
  onAssociationToggle,
  savedAssociation,
}: ResultRowProps) {
  const { text: t } = useUiLanguage();
  if (!item) {
    return (
      <div className="result-row" data-digits={digits}>
        <span className="loading-text">...</span>
      </div>
    );
  }
  if (!item.ok) {
    return (
      <div className="result-row result-row-error" data-digits={digits}>
        {item.error}
      </div>
    );
  }
  return (
    <div
      className={`result-row${randomRemappingEnabled ? " result-row-remappable" : ""}`}
      data-digits={digits}
      onMouseEnter={() => onAssociationHover(true)}
      onMouseLeave={() => onAssociationHover(false)}
    >
      <AssociationStarButton
        busy={associationBusy}
        className="association-star-mobile"
        digits={digits}
        onClick={onAssociationToggle}
        saved={savedAssociation !== undefined}
        words={item.result.text}
      />
      <span className="result-words">
        {item.result.candidates.map((candidate, candidateIndex) => (
          <WordWithPreview
            activeHoverWindow={activeHoverWindow}
            key={`${candidateIndex}-${candidate.number}-${candidate.noun}-${candidate.noun_qid}`}
            candidate={candidate}
            encodingSystem={encodingSystem}
            hoverWindowId={`word-preview:${rowIndex}:${candidateIndex}`}
            onHoverWindowClose={onHoverWindowClose}
            onHoverWindowOpen={onHoverWindowOpen}
            summary={
              wikiCache[wikipediaCacheKey(
                encodingSystem,
                candidate.noun,
                candidate.wikipedia_url,
              )]
            }
            onPreview={() =>
              onWordPreview(candidate.noun, candidate.wikipedia_url)
            }
            wikipediaPreviewsEnabled={wikipediaPreviewsEnabled}
          />
        ))}
      </span>
      {randomRemappingEnabled ? (
        <button
          className={`remap-button${remapping ? " remap-button-active" : ""}`}
          type="button"
          aria-label={t("remapRandomly", { digits })}
          title={t("tryAnotherMapping")}
          disabled={remapping}
          onClick={onRandomRemap}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M19 7v5h-5M5 17v-5h5M7.1 9a6 6 0 0 1 9.8-2L19 9M5 15l2.1 2a6 6 0 0 0 9.8-2" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

type AssociationGutterProps = {
  activeRow: number | null;
  busyRows: Set<number>;
  digits: string[];
  items: BatchEncodeItem[];
  limit: number;
  onHoverRow: (row: number | null) => void;
  onToggle: (row: number) => void;
  savedAssociations: Array<SavedAssociation | undefined>;
  total: number;
};

function AssociationGutter({
  activeRow,
  busyRows,
  digits,
  items,
  limit,
  onHoverRow,
  onToggle,
  savedAssociations,
  total,
}: AssociationGutterProps) {
  const { text: t } = useUiLanguage();
  return (
    <aside
      className="association-gutter"
      aria-label={t("savedAssociationsCount", { total, limit })}
    >
      <div className="panel-title" aria-hidden="true"> </div>
      <div className="association-list">
        {digits.map((number, index) => {
          const item = items[index]?.digits === number ? items[index] : undefined;
          return (
            <div
              className={`association-row${activeRow === index ? " association-row-active" : ""}`}
              key={`${number}-${index}`}
              onMouseEnter={() => onHoverRow(index)}
              onMouseLeave={() => onHoverRow(null)}
            >
              {item?.ok ? (
                <AssociationStarButton
                  busy={busyRows.has(index)}
                  className="association-star-gutter"
                  digits={number}
                  onClick={() => onToggle(index)}
                  saved={savedAssociations[index] !== undefined}
                  words={item.result.text}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

type AssociationStarButtonProps = {
  busy: boolean;
  className: string;
  digits: string;
  onClick: () => void;
  saved: boolean;
  words: string;
};

function AssociationStarButton({
  busy,
  className,
  digits,
  onClick,
  saved,
  words,
}: AssociationStarButtonProps) {
  const { text: t } = useUiLanguage();
  const action = saved ? t("removeSaved") : t("save");
  return (
    <button
      className={`association-star ${className}${saved ? " association-star-saved" : ""}`}
      type="button"
      aria-label={t("savedAssociationLabel", { action, digits, words })}
      title={t("savedAssociationTitle", { action })}
      disabled={busy}
      onClick={onClick}
    >
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="m12 2.8 2.75 5.57 6.15.9-4.45 4.33 1.05 6.12L12 16.83l-5.5 2.89 1.05-6.12L3.1 9.27l6.15-.9L12 2.8Z" />
      </svg>
    </button>
  );
}

type WordWithPreviewProps = {
  activeHoverWindow: string | null;
  candidate: Candidate;
  encodingSystem: EncodingSystem;
  hoverWindowId: string;
  onHoverWindowClose: (windowId: string) => void;
  onHoverWindowOpen: (windowId: string) => void;
  summary: WikipediaSummary | null | undefined;
  onPreview: () => void;
  wikipediaPreviewsEnabled: boolean;
};

type WordPreviewPlacement = {
  above: boolean;
  centered: boolean;
  left: number;
  maxHeight: number;
  top: number;
  width?: number;
};

function WordWithPreview({
  activeHoverWindow,
  candidate,
  encodingSystem,
  hoverWindowId,
  onHoverWindowClose,
  onHoverWindowOpen,
  summary,
  onPreview,
  wikipediaPreviewsEnabled,
}: WordWithPreviewProps) {
  const { text: t } = useUiLanguage();
  const encodingProfile = getEncodingProfile(encodingSystem);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [placement, setPlacement] = useState<WordPreviewPlacement | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [closeButtonVisible, setCloseButtonVisible] = useState(false);
  const previewOpenRef = useRef(false);
  const closeTimer = useRef<number | undefined>(undefined);
  const closeButtonTimer = useRef<number | undefined>(undefined);
  const exactWikipediaUrl = wikipediaTitleFromPageUrl(
    candidate.wikipedia_url,
    encodingSystem,
  ) === null ? null : candidate.wikipedia_url;
  const href = exactWikipediaUrl ??
    summary?.content_urls?.desktop?.page ??
    wikipediaPageUrl(candidate.noun, encodingSystem);
  const disambiguationExtract = summary?.disambiguation_extract;

  useEffect(
    () => () => {
      window.clearTimeout(closeTimer.current);
      window.clearTimeout(closeButtonTimer.current);
    },
    [],
  );

  function cancelPreviewClose() {
    window.clearTimeout(closeTimer.current);
  }

  function showPreview(pointer?: { clientX: number; clientY: number }) {
    cancelPreviewClose();
    onHoverWindowOpen(hoverWindowId);
    onPreview();
    const bounds = buttonRef.current?.getBoundingClientRect();
    if (!bounds) return;
    if (!previewOpenRef.current) {
      previewOpenRef.current = true;
      setPreviewOpen(true);
      setCloseButtonVisible(false);
      window.clearTimeout(closeButtonTimer.current);
      closeButtonTimer.current = window.setTimeout(
        () => setCloseButtonVisible(true),
        2000,
      );
    }
    if (pointer) {
      const horizontalHalfSpace = Math.max(
        1,
        Math.min(pointer.clientX - 12, window.innerWidth - pointer.clientX - 12),
      );
      const verticalHalfSpace = Math.max(
        1,
        Math.min(pointer.clientY - 12, window.innerHeight - pointer.clientY - 12),
      );
      setPlacement({
        above: false,
        centered: true,
        left: pointer.clientX,
        maxHeight: Math.min(window.innerHeight - 24, verticalHalfSpace * 2),
        top: pointer.clientY,
        width: Math.min(420, window.innerWidth - 32, horizontalHalfSpace * 2),
      });
      return;
    }
    const above = bounds.top + bounds.height / 2 > window.innerHeight / 2;
    const cardWidth = Math.min(420, window.innerWidth - 24);
    setPlacement({
      above,
      centered: false,
      left: Math.max(
        12,
        Math.min(bounds.left, window.innerWidth - cardWidth - 12),
      ),
      maxHeight: Math.max(
        180,
        above
          ? bounds.top - 16
          : window.innerHeight - bounds.bottom - 16,
      ),
      top: above ? bounds.top - 10 : bounds.bottom + 10,
    });
  }

  function hidePreview() {
    previewOpenRef.current = false;
    window.clearTimeout(closeTimer.current);
    window.clearTimeout(closeButtonTimer.current);
    setCloseButtonVisible(false);
    setPreviewOpen(false);
    setPlacement(null);
    onHoverWindowClose(hoverWindowId);
  }

  function schedulePreviewClose() {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(
      hidePreview,
      WORD_PREVIEW_CLOSE_DELAY_MS,
    );
  }

  return (
    <span
      className="word-shell"
      onMouseEnter={cancelPreviewClose}
      onMouseLeave={schedulePreviewClose}
    >
      <button
        ref={buttonRef}
        className="word-link"
        type="button"
        onMouseEnter={
          wikipediaPreviewsEnabled ? () => showPreview() : undefined
        }
        onFocus={wikipediaPreviewsEnabled ? () => showPreview() : undefined}
        onClick={
          wikipediaPreviewsEnabled
            ? (event: ReactMouseEvent<HTMLButtonElement>) => showPreview(event)
            : undefined
        }
      >
        {candidate.noun}
      </button>
      {wikipediaPreviewsEnabled &&
      previewOpen &&
      placement &&
      activeHoverWindow === hoverWindowId ? (
        <span
          className={`wiki-preview${placement.above ? " wiki-preview-above" : ""}${placement.centered ? " wiki-preview-pointer-centered" : ""}`}
          role="tooltip"
          style={{
            left: placement.left,
            maxHeight: placement.maxHeight,
            top: placement.top,
            width: placement.width,
          }}
          onMouseEnter={cancelPreviewClose}
          onMouseLeave={schedulePreviewClose}
        >
          {closeButtonVisible ? (
            <button
              className="preview-close-button"
              type="button"
              aria-label={t("closePreview")}
              onClick={hidePreview}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          ) : null}
          {summary?.thumbnail?.source ? (
            <img src={summary.thumbnail.source} alt="" />
          ) : null}
          <strong>{summary?.title ?? candidate.noun}</strong>
          <span className="wiki-preview-label">{t("number")}</span>
          <span className="wiki-preview-number">{candidate.number}</span>
          {encodingProfile.showsPronunciationMetadata ? (
            <>
              <span className="wiki-preview-label">{t("dialect")}</span>
              <span className="wiki-preview-dialect">
                {pronunciationDialectLabel(candidate.pronunciation_dialect) ??
                  t("unavailable")}
              </span>
              <span className="wiki-preview-label">{t("pronunciation")}</span>
              <span>
                {candidate.pronunciation ?? t("unavailable")}
                {candidate.pronunciation_alphabet
                  ? ` (${candidate.pronunciation_alphabet.toUpperCase()})`
                  : ""}
              </span>
            </>
          ) : null}
          {summary === undefined ? (
            <span className="preview-muted">{t("loadingPreview")}</span>
          ) : summary === null &&
            getEncodingProfile(encodingSystem).requiresLocalizedCandidateMetadata &&
            candidate.definition ? (
            <>
              <span className="wiki-preview-label">{t("definition")}</span>
              <span>{candidate.definition}</span>
            </>
          ) : summary === null ? (
            <span className="preview-muted">{t("noExactWikipedia")}</span>
          ) : (
            <>
            <span className="wiki-preview-label">{t("definition")}</span>
            <span className={disambiguationExtract ? "wiki-preview-full-extract" : undefined}>
              {disambiguationExtract ?? shortenWikipediaExtract(summary.extract)}
            </span>
            {disambiguationExtract ? (
              <span className="wiki-preview-attribution">
                {t("sourceWikipedia")} {" "}
                <a
                  href="https://creativecommons.org/licenses/by-sa/4.0/"
                  target="_blank"
                  rel="noreferrer"
                >
                  CC BY-SA 4.0
                </a>
              </span>
            ) : null}
            </>
          )}
          <a
            className="wiki-preview-page-link"
            href={href}
            target="_blank"
            rel="noreferrer"
          >
            {t("openWikipediaPage")}
          </a>
        </span>
      ) : null}
    </span>
  );
}

function HelpDialog({
  encodingSystem,
  onClose,
}: {
  encodingSystem: EncodingSystem;
  onClose: () => void;
}) {
  const { language, text: t } = useUiLanguage();
  const help = getEncodingHelp(encodingSystem, language);
  const features = help.full;
  return (
    <div className="dialog-backdrop">
      <section
        className="help-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-dialog-title"
      >
        <div className="dialog-heading">
          <div>
            <h1 id="help-dialog-title">{t("help")}</h1>
            <p>{t("helpIntroduction")}</p>
          </div>
          <button className="secondary-button" type="button" onClick={onClose}>
            {t("close")}
          </button>
        </div>
        <article className="help-mapping-rules" aria-label={t("majorSystemRules")}>
          <h2>{help.compact.title}</h2>
          <p>{help.compact.introduction}</p>
          <ul className="major-system-map">
            {help.compact.mapping.map(({ description, digit }) => (
              <li key={digit}>
                <strong>{digit}</strong>
                <span>{description}</span>
              </li>
            ))}
          </ul>
          <p>{help.compact.notes}</p>
          <a href={help.compact.wikipediaUrl} target="_blank" rel="noreferrer">
            {t("wikipedia")}
          </a>
        </article>
        <div className="help-feature-grid">
          {features.map((feature) => (
            <article
              className={`help-feature${feature.images.length > 1 ? " help-feature-wide" : ""}`}
              key={feature.title}
            >
              <h2>{feature.title}</h2>
              <p>{feature.description}</p>
              <p className="help-feature-sample">{feature.sample}</p>
              <div
                className={`help-feature-images${feature.images.length > 1 ? " help-feature-images-pair" : ""}`}
              >
                {feature.images.map((featureImage) => (
                  <figure key={featureImage.src}>
                    <img src={featureImage.src} alt={featureImage.alt} />
                    {featureImage.caption ? (
                      <figcaption>{featureImage.caption}</figcaption>
                    ) : null}
                  </figure>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function ContactsDialog({
  notice,
  onClose,
}: {
  notice: PrivacyNotice | null;
  onClose: () => void;
}) {
  const { text: t } = useUiLanguage();
  const privacyEmail = notice?.privacy_contact_email ?? "kmavropulo@outlook.com";
  const engineeringEmail = notice?.engineering_contact_email ?? privacyEmail;
  const controllerUrl = notice?.controller_profile_url || "https://kmavropulo.com";
  return (
    <div className="dialog-backdrop">
      <section
        className="contacts-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contacts-dialog-title"
      >
        <div className="dialog-heading">
          <h1 id="contacts-dialog-title">{t("contacts")}</h1>
          <button className="secondary-button" type="button" onClick={onClose}>
            {t("close")}
          </button>
        </div>
        <address className="contact-block">
          <h2>{t("authorController")}</h2>
          <p className="contact-profile-row">
            <a href={controllerUrl} target="_blank" rel="noreferrer">kmavropulo.com</a>
            <a
              className="contact-social-link"
              href="https://linkedin.com/in/kmavropulo?trk=profile-badge"
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
              title="LinkedIn"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.32 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12ZM7.1 20.45H3.54V9H7.1v11.45Z" />
              </svg>
            </a>
          </p>
          <p><strong>{t("privacy")}</strong><br /><a href={`mailto:${privacyEmail}`}>{privacyEmail}</a></p>
          <p><strong>{t("engineeringSupport")}</strong><br /><a href={`mailto:${engineeringEmail}`}>{engineeringEmail}</a></p>
        </address>
      </section>
    </div>
  );
}

type AccountDialogProps = {
  activeTab: AccountTab;
  notice: PrivacyNotice | null;
  user: User | null;
  productEmailsOptIn: boolean;
  savedAssociationLimit: number;
  savedAssociations: SavedAssociation[];
  eraseConfirmation: boolean;
  status: string;
  onClose: () => void;
  onContacts: () => void;
  onPrivacy: () => void;
  onTerms: () => void;
  onExport: () => void;
  onExportSavedAssociations: () => void;
  onErase: () => void;
  onProductEmails: (enabled: boolean) => void;
  onTabChange: (tab: AccountTab) => void;
};

function AccountDialog({
  activeTab,
  notice,
  user,
  productEmailsOptIn,
  savedAssociationLimit,
  savedAssociations,
  eraseConfirmation,
  status,
  onClose,
  onContacts,
  onPrivacy,
  onTerms,
  onExport,
  onExportSavedAssociations,
  onErase,
  onProductEmails,
  onTabChange,
}: AccountDialogProps) {
  const { text: t } = useUiLanguage();
  const signedIn = user !== null;
  return (
    <div className="dialog-backdrop">
      <section
        className={`account-dialog${signedIn ? " account-dialog-signed-in" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={signedIn
          ? t("userAccount")
          : activeTab === "terms"
            ? t("termsOfService")
            : t("privacyNotice")}
      >
        <div className="dialog-heading">
          <h1>{signedIn
            ? t("userAccount")
            : activeTab === "terms"
              ? t("termsOfService")
              : t("privacyTitle")}</h1>
          <button className="secondary-button" type="button" onClick={onClose}>
            {t("close")}
          </button>
        </div>
        <div className={`account-layout${signedIn ? "" : " account-layout-privacy-only"}`}>
          {signedIn ? (
            <nav className="account-tabs" role="tablist" aria-label={t("accountSections")}>
              {(["profile", "data", "terms", "privacy", "delete"] as const).map((tab) => (
                <button
                  id={`account-tab-${tab}`}
                  className="account-tab"
                  type="button"
                  role="tab"
                  aria-controls={`account-panel-${tab}`}
                  aria-selected={activeTab === tab}
                  key={tab}
                  onClick={() => onTabChange(tab)}
                >
                  {tab === "profile"
                    ? t("accountProfile")
                    : tab === "data"
                      ? t("accountData")
                      : tab === "terms"
                        ? t("accountTerms")
                        : tab === "privacy"
                          ? t("accountPrivacy")
                          : t("accountDelete")}
                </button>
              ))}
            </nav>
          ) : null}
          <div
            id={`account-panel-${activeTab}`}
            className="account-details"
            role={signedIn ? "tabpanel" : undefined}
            aria-labelledby={signedIn ? `account-tab-${activeTab}` : undefined}
          >
            {signedIn && activeTab === "profile" && user ? (
              <ProfilePanel
                user={user}
                productEmailsOptIn={productEmailsOptIn}
                onProductEmails={onProductEmails}
              />
            ) : null}
            {signedIn && activeTab === "data" ? (
              <AccountDataPanel
                savedAssociationLimit={savedAssociationLimit}
                savedAssociations={savedAssociations}
                onExport={onExport}
                onExportSavedAssociations={onExportSavedAssociations}
              />
            ) : null}
            {activeTab === "terms" ? (
              <TermsDetails onContacts={onContacts} onPrivacy={onPrivacy} />
            ) : null}
            {activeTab === "privacy" ? (
              <PrivacyDetails
                notice={notice}
                onContacts={onContacts}
                onTerms={onTerms}
              />
            ) : null}
            {signedIn && activeTab === "delete" ? (
              <section className="delete-account-panel">
                <h2>{t("deleteAccount")}</h2>
                <h3>{t("permanentDeletion")}</h3>
                <p>{t("deleteAccountDescription")}</p>
                <button className="danger-button" type="button" onClick={onErase}>
                  {eraseConfirmation ? t("confirmAccountDeletion") : t("deleteMyAccount")}
                </button>
              </section>
            ) : null}
            {status ? <p role="status">{status}</p> : null}
          </div>
        </div>
      </section>
    </div>
  );
}

function TermsDetails({
  onContacts,
  onPrivacy,
}: {
  onContacts: () => void;
  onPrivacy: () => void;
}) {
  const { language, text: t } = useUiLanguage();
  const content = getTermsContent(language);
  return (
    <section>
      <h2>{t("termsOfService")}</h2>
      <p><strong>{t("version")}</strong> {TERMS_VERSION}</p>
      <p><strong>{t("effectiveDate")}</strong> {TERMS_VERSION}</p>
      <h3>{t("usingService")}</h3>
      {content.usingService.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <h3>{t("availableSupport")}</h3>
      {content.availableSupport.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <h3>{t("savedMappingsExports")}</h3>
      {content.savedMappingsExports.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <h3>{t("changesSuspension")}</h3>
      {content.changesSuspension.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <h3>{t("accountTermination")}</h3>
      {content.accountTermination.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <h3>{t("noWarranties")}</h3>
      {content.noWarranties.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <h3>{t("limitationLiability")}</h3>
      {content.limitationLiability.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <h3>{t("mandatoryRights")}</h3>
      {content.mandatoryRights.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <h3>{t("governingLaw")}</h3>
      {content.governingLaw.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <h3>{t("changesTerms")}</h3>
      {content.changesTerms.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <h3>{t("privacyAndContact")}</h3>
      <p>
        {content.privacyAndContact.beforePrivacy}
        <a
          href="/?legal=privacy"
          onClick={(event) => {
            event.preventDefault();
            onPrivacy();
          }}
        >
          {t("privacy")}
        </a>
        {content.privacyAndContact.betweenLinks}
        <a
          href="/?legal=contacts"
          onClick={(event) => {
            event.preventDefault();
            onContacts();
          }}
        >
          {t("contacts")}
        </a>
        {content.privacyAndContact.afterContacts}
      </p>
    </section>
  );
}

type AccountDataPanelProps = {
  savedAssociationLimit: number;
  savedAssociations: SavedAssociation[];
  onExport: () => void;
  onExportSavedAssociations: () => void;
};

function AccountDataPanel({
  savedAssociationLimit,
  savedAssociations,
  onExport,
  onExportSavedAssociations,
}: AccountDataPanelProps) {
  const { language, text: t } = useUiLanguage();
  return (
    <section>
      <h2>{t("accountData")}</h2>
      <h3>{t("export")}</h3>
      <p>{t("downloadCopy")}</p>
      <div className="account-export-actions">
        <button className="secondary-button" type="button" onClick={onExport}>
          {t("downloadAccountJson")}
        </button>
        <button
          className="secondary-button"
          type="button"
          onClick={onExportSavedAssociations}
        >
          {t("downloadSavedCsv")}
        </button>
      </div>
      <h3>{t("savedNumbers")}</h3>
      <dl className="account-summary account-summary-compact">
        <div>
          <dt>{t("savedAssociations")}</dt>
          <dd>{savedAssociations.length} of {savedAssociationLimit}</dd>
        </div>
      </dl>
      {savedAssociations.length === 0 ? (
        <p>{t("noSavedAssociations")}</p>
      ) : (
        <div className="saved-number-table-wrap">
          <table className="saved-number-table">
            <thead>
              <tr><th>{t("number")}</th><th>{t("mapping")}</th><th>{t("saved")}</th></tr>
            </thead>
            <tbody>
              {savedAssociations.map((association) => (
                <tr key={association.id}>
                  <td>{association.digits}</td>
                  <td>{association.candidates.map((candidate) => candidate.noun).join(" ")}</td>
                  <td>{new Date(association.created_at).toLocaleString(language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type ProfilePanelProps = {
  user: User;
  productEmailsOptIn: boolean;
  onProductEmails: (enabled: boolean) => void;
};

function ProfilePanel({
  user,
  productEmailsOptIn,
  onProductEmails,
}: ProfilePanelProps) {
  const { text: t } = useUiLanguage();
  return (
    <section>
      <h2>{t("accountProfile")}</h2>
      <h3>{t("personalInformation")}</h3>
      <dl className="account-summary account-summary-profile">
        <div><dt>{t("email")}</dt><dd>{user.email}</dd></div>
        {user.role === "admin" ? (
          <div><dt>{t("role")}</dt><dd>{t("admin")}</dd></div>
        ) : null}
      </dl>
      <h3>{t("communicationPreferences")}</h3>
      <label className="account-email-choice">
        <input
          type="checkbox"
          checked={productEmailsOptIn}
          onChange={(event) => onProductEmails(event.target.checked)}
        />
        {t("emailProductNews")}
      </label>
    </section>
  );
}

function PrivacyDetails({
  notice,
  onContacts,
  onTerms,
}: {
  notice: PrivacyNotice | null;
  onContacts: () => void;
  onTerms: () => void;
}) {
  const { language, text: t } = useUiLanguage();
  if (!notice) {
    return (
      <p>{t("privacyNoticeUnavailable")}</p>
    );
  }
  const content = getPrivacyContent(notice, language);
  return (
    <section>
      <h2>{t("privacyTitle")}</h2>
      {!notice.configured_for_public_use ? (
        <p className="privacy-warning">
          {t("unconfiguredDeployment")}
        </p>
      ) : null}
      <p><strong>{t("effectiveDate")}</strong> {notice.effective_date}</p>
      <p><strong>{t("lastUpdated")}</strong> {notice.last_updated}</p>
      <h3>{t("whatIsProcessed")}</h3>
      <ul>{content.personalDataCollected.map((item) => <li key={item}>{item}</li>)}</ul>
      <h3>{t("whatIsNotCollected")}</h3>
      <ul>{content.dataNotCollected.map((item) => <li key={item}>{item}</li>)}</ul>
      <h3>{t("sourcesData")}</h3>
      <ul>{content.dataSources.map((item) => <li key={item}>{item}</li>)}</ul>
      <h3>{t("requiredOptionalData")}</h3>
      <ul>{content.requiredPersonalData.map((item) => <li key={item}>{item}</li>)}</ul>
      <h3>{t("purposesLegalBases")}</h3>
      <ul>{content.purposesAndLegalBases.map((item) => <li key={item}>{item}</li>)}</ul>
      <h3>{t("cookies")}</h3>
      {content.cookies.map((cookie) => (
        <p key={cookie.name}>
          <strong>{cookie.name}</strong>: {cookie.description}
        </p>
      ))}
      <h3>{t("recipientsExternal")}</h3>
      {content.recipients.map((party) => (
        <p key={party.name}>
          <strong>{party.name}</strong>: {party.description}
          {party.privacyUrl ? (
            <> <a href={party.privacyUrl} target="_blank" rel="noreferrer">{t("privacyInformation")}</a></>
          ) : null}
        </p>
      ))}
      <h3>{t("retention")}</h3>
      <ul>{content.retention.map((item) => <li key={item}>{item}</li>)}</ul>
      <h3>{t("internationalTransfers")}</h3>
      <ul>{content.internationalTransfers.map((item) => <li key={item}>{item}</li>)}</ul>
      <h3>{t("security")}</h3>
      <ul>{content.securityMeasures.map((item) => <li key={item}>{item}</li>)}</ul>
      <h3>{t("usPrivacy")}</h3>
      <p>{content.usPrivacyDisclosure}</p>
      <h3>{t("automatedDecisions")}</h3>
      <p>{content.automatedDecisionMaking}</p>
      <h3>{t("rights")}</h3>
      <ul>{content.rights.map((item) => <li key={item}>{item}</li>)}</ul>
      <p>{content.complaintGuidance}</p>
      <h3>{t("changesToNotice")}</h3>
      <p>{content.materialChanges}</p>
      <h3>{t("children")}</h3>
      <p>{content.childrenPolicy}</p>
      <h3>{t("serviceClosure")}</h3>
      <p>
        {content.serviceClosure} {content.serviceClosureLink.beforeTerms}
        <a
          href="/?legal=terms"
          onClick={(event) => {
            event.preventDefault();
            onTerms();
          }}
        >
          {t("termsOfService")}
        </a>
        {content.serviceClosureLink.afterTerms}
      </p>
      <h3>{t("controller")}</h3>
      <p>
        {content.controllerLink.beforeContacts}
        <a
          href="/?legal=contacts"
          onClick={(event) => {
            event.preventDefault();
            onContacts();
          }}
        >
          {t("contacts")}
        </a>
        {content.controllerLink.afterContacts}
      </p>
    </section>
  );
}

export default App;
